import clsx from "clsx";
import { FC } from "react";
import Highlighter from "react-highlight-words";

import { STRUCTURE_DEPTH_MAX } from "./ai-conversation.constants";
import { IStructureNode } from "./ai-conversation.models";
import {
  buildStructureTree,
  formatValueForDisplay,
  formatValueForTooltip,
  getColorKeyForClass,
  getEmojiForType,
  getTypeClass,
} from "./ai-conversation.utils";
import { StructureNodeTooltip } from "./structure-node-tooltip";

interface IAiConversationDetailsStructureProps {
  fullInfoSrc: unknown[];
  searchTerm?: string;
}

export const AiConversationDetailsStructure: FC<
  IAiConversationDetailsStructureProps
> = ({ fullInfoSrc, searchTerm }) => {
  const nodes: IStructureNode[] = buildStructureTree(fullInfoSrc);

  const filteredNodes = searchTerm
    ? nodes.filter(node => {
        const keyMatch = node.key
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
        const typeMatch = node.type
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase());
        const valueMatch =
          node.value !== undefined &&
          String(node.value).toLowerCase().includes(searchTerm.toLowerCase());
        return keyMatch || typeMatch || valueMatch;
      })
    : nodes;

  if (filteredNodes.length === 0) {
    return <span>—</span>;
  }

  const renderNode = (node: IStructureNode, i: number): JSX.Element => {
    const colorKey = getColorKeyForClass(node.key);
    const emoji = getEmojiForType(node.type);

    const isPrimitive =
      node.type === "string" ||
      node.type === "number" ||
      node.type === "boolean" ||
      node.type === "null";
    const isComplexType = !node.type || node.type === "[]";

    const tooltipContent =
      isComplexType && node.value !== undefined
        ? formatValueForTooltip(node.value)
        : "";

    const displayValue =
      isPrimitive && node.value !== undefined
        ? formatValueForDisplay(node.value)
        : "";

    const keyContent = searchTerm ? (
      <Highlighter
        searchWords={[searchTerm]}
        autoEscape={true}
        textToHighlight={node.key}
        highlightClassName="search-highlight"
      />
    ) : (
      node.key
    );

    let valueContent = null;
    if (displayValue) {
      const formattedValue =
        node.type === "string" ? `"${displayValue}"` : displayValue;
      if (searchTerm) {
        valueContent = (
          <Highlighter
            searchWords={[searchTerm]}
            autoEscape={true}
            textToHighlight={formattedValue}
            highlightClassName="search-highlight"
          />
        );
      } else {
        valueContent = formattedValue;
      }
    }

    const keyElement = (
      <>
        <span className={clsx("structure-key", `structure-key-${colorKey}`)}>
          {keyContent}
        </span>
        {!isPrimitive && <span className="structure-emoji">{emoji}</span>}
      </>
    );

    const depthClass = `structure-depth-${Math.min(node.depth, STRUCTURE_DEPTH_MAX)}`;

    return (
      <div key={i} className={clsx("structure-tree-line", depthClass)}>
        {isComplexType ? (
          <StructureNodeTooltip content={tooltipContent}>
            <span className="structure-key-wrapper">{keyElement}</span>
          </StructureNodeTooltip>
        ) : (
          <span className="structure-key-wrapper">{keyElement}</span>
        )}
        {!!node.type && (
          <span className={`structure-type ${getTypeClass(node.type)}`}>
            {node.type}
          </span>
        )}
        {valueContent && (
          <span className="structure-value">{valueContent}</span>
        )}
      </div>
    );
  };

  return <>{filteredNodes.map(renderNode)}</>;
};
